/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import router from "@/router";
import { generalStore, userStore } from "@/store";
import { User } from "monkeybattle-shared";

import * as waxjs from "@waxio/waxjs/dist";
import { Api, JsonRpc } from "eosjs";
import { JsSignatureProvider } from "eosjs/dist/eosjs-jssig";
import api from "./api.service";

const eosEndpoint = new JsonRpc("https://wax.greymass.com");
const signatureProvider = new JsSignatureProvider([]);
const eosApi = new Api({ rpc: eosEndpoint, signatureProvider });

const endpoint = new waxjs.WaxJS({
  rpcEndpoint: "https://wax.greymass.com",
});

import AnchorLink, { LinkSession } from "anchor-link";
import AnchorLinkBrowserTransport from "anchor-link-browser-transport";
import { LeaderboardUser } from "@/types";
import { GameRecord } from "@/types/gameRecord";
const transport = new AnchorLinkBrowserTransport();
const link = new AnchorLink({
  transport,
  chains: [
    {
      chainId:
        "1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4",
      nodeUrl: "https://wax.greymass.com",
    },
  ],
});

let linkSession: LinkSession;

export class TransactionNotSignedError extends Error {}

interface VerifyNonceResponse {
  user: User;
  numDecksRemoved: number;
  decksAltered: boolean;
}

export const userService = {
  async setStoreUser(): Promise<void> {
    try {
      const user = await api.get<User>("/user/self");
      userStore.mutations.setUser(user);
    } catch (e) {
      router.push("/");
    }
  },

  async loginWax(): Promise<string> {
    const userAccount = await endpoint.login();
    if (!userAccount) throw new Error("Could not log in");
    return userAccount as string;
  },

  async loginAnchor(): Promise<string> {
    const identity = await link.login("MonkeyBattle");
    linkSession = identity.session;
    return identity.session.auth.actor.toString();
  },

  async setBanAddress(address: string): Promise<void> {
    return await api.post("/user/ban", {
      address,
    });
  },

  async getNonce(userAccount: string): Promise<string> {
    const nonce = await api.post<string>("/user/getNonce", {
      userAccount,
    });
    if (!nonce) throw new Error("Could not get nonce from server");
    userStore.mutations.setNonce(nonce);
    return nonce;
  },

  async logout() {
    return await api.post("/user/logout");
  },

  getTransactionData(userAccount: string) {
    return {
      data: {
        actions: [
          {
            account: "orng.wax",
            name: "requestrand",
            authorization: [
              {
                actor: userAccount,
                permission: "active",
              },
            ],
            data: {
              caller: userAccount,
              signing_value: userStore.getters.nonce(),
              assoc_id: userStore.getters.nonce(),
            },
          },
        ],
      },
      options: {
        blocksBehind: 3,
        expireSeconds: 30,
        broadcast: false,
        sign: true,
      },
    };
  },

  async signWax(userAccount: string): Promise<void> {
    const txData = this.getTransactionData(userAccount);
    const transaction = await endpoint.api.transact(
      txData.data,
      txData.options
    );

    if (!("signatures" in transaction) || !transaction.signatures[0]) {
      throw new TransactionNotSignedError("Transaction was not signed");
    }

    const { user, numDecksRemoved, decksAltered } =
      await api.post<VerifyNonceResponse>("/user/verifyNonce", { transaction });
    userStore.mutations.setUser(user);
    generalStore.mutations.setNumDecksRemoved(numDecksRemoved);
    generalStore.mutations.setDecksAltered(decksAltered);
  },

  async signAnchor(userAccount: string): Promise<void> {
    if (!linkSession) throw new Error("Link Session not defined");
    const txData = this.getTransactionData(userAccount);
    const tx = await linkSession.transact(txData.data, txData.options);

    const transaction = JSON.parse(JSON.stringify(tx.transaction));
    console.log(transaction);

    const { user, numDecksRemoved, decksAltered } =
      await api.post<VerifyNonceResponse>("/user/verifyNonce", {
        transaction: {
          serializedTransaction: eosApi.serializeTransaction(
            JSON.parse(JSON.stringify(tx.transaction))
          ),
          signatures: tx.signatures,
        },
      });
    userStore.mutations.setUser(user);
    generalStore.mutations.setNumDecksRemoved(numDecksRemoved);
    generalStore.mutations.setDecksAltered(decksAltered);
  },

  async getAll(limit = 20, offset = 0) {
    return await api.get<User[]>("/user/all", {
      params: {
        limit,
        offset,
        orderBy: "xp",
        order: "desc",
      },
    });
  },

  async getTopByXp(limit = 20, offset = 0, n = 7) {
    return await api.get<LeaderboardUser[]>("game-record/leaderboard", {
      params: {
        limit,
        offset,
        n,
      },
    });
  },

  async getRecentGames() {
    return await api.get<GameRecord[]>("game-record", {
      params: { n: 5 },
    });
  },

  monkeyconnectClientId: "ohWXcNHvNoXamjqlVQkmplgygwjHDaYhfTnTA5Yh",
  syncBanMonkeyconnect() {
    window.location.href = `https://connect.cryptomonkeys.cc/o/authorize?response_type=code&client_id=${this.monkeyconnectClientId}&scope=ban&state=1234`;
  },
};
